import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { Kapitel } from '../types'
import { kapitelIndex, laddaKapitel } from '../lib/content'
import { useSidTitel } from '../lib/seo'
import MarkeradText from '../components/MarkeradText'
import Avsnitt from '../components/Avsnitt'
import { AplMaterialInnehall } from '../components/AplMaterialVy'
import { KURSNAMN, POANG_TEXT } from '../lib/site'

/** Utskriftsvy för hela läromedlet: alla publicerade kapitel efter varandra
 *  med samma utskriftsformat som kapitelsidornas Skriv ut-knapp — kapiteltext
 *  följt av begrepp, instuderingsfrågor och övningsuppgifter på egna sidor.
 *  Quiz och facit skrivs inte ut. Sidan är noindex i förrenderingen (den
 *  duplicerar kapitlens innehåll) och förladdas därför inte. */
export default function SkrivUtAlla() {
  const [kapitel, setKapitel] = useState<Kapitel[] | null>(null)
  useSidTitel('Skriv ut hela läromedlet')

  useEffect(() => {
    let aktiv = true
    const publicerade = kapitelIndex.filter((k) => k.publicerad && k.slug)
    Promise.all(publicerade.map((k) => laddaKapitel(k.slug!))).then((resultat) => {
      if (aktiv) setKapitel(resultat.filter((k): k is Kapitel => k !== null))
    })
    return () => {
      aktiv = false
    }
  }, [])

  if (!kapitel) {
    return (
      <div className="wrap">
        <p className="dokument">Hämtar alla kapitel …</p>
      </div>
    )
  }

  return (
    <div className="wrap">
      <div className="bok-verktygsrad no-print">
        <nav className="brodsmula" aria-label="Brödsmulor">
          <Link to="/">← Till startsidan</Link>
        </nav>
        <div>
          <button type="button" className="knapp knapp-primar" onClick={() => window.print()}>
            🖨 Skriv ut hela läromedlet
          </button>
        </div>
      </div>
      <p className="sektionsingress no-print">
        Den här sidan samlar alla {kapitel.length} publicerade kapitel för utskrift: kapiteltext
        följt av begrepp, instuderingsfrågor och övningsuppgifter. Quiz och facit skrivs inte ut.
      </p>

      <section className="bok-titelsida">
        <span className="hero-etikett">Gymnasiet · Gy25{POANG_TEXT ? ` · ${POANG_TEXT}` : ''}</span>
        <h1>{KURSNAMN}</h1>
        <p>Digitalt läromedel för kursen {KURSNAMN}.</p>
        <ol className="bok-innehall">
          {kapitel.map((k) => (
            <li key={k.id}>{k.titel}</li>
          ))}
        </ol>
      </section>

      {kapitel.map((k) => (
        <article key={k.id} className="bok-kapitel print-nysida">
          <header className="kapitelhuvud">
            <span className="kapitel-nr">Kapitel {k.id}</span>
            <h1>{k.titel}</h1>
            <div className="larandemal">
              <h2>Efter kapitlet ska du kunna</h2>
              <ul>
                {k.larandemal.map((mal) => (
                  <li key={mal}>{mal.replace(/\s*\(F[1-4](,\s*F[1-4])*\)\s*$/, '')}</li>
                ))}
              </ul>
            </div>
          </header>

          <div className="brodtext">
            <MarkeradText markdown={k.intro} styckeKlass="ingress" />
            {k.avsnitt.map((avsnitt) => (
              <Avsnitt key={avsnitt.rubrik} avsnitt={avsnitt} />
            ))}
          </div>

          <section className="sektion sektion-begrepp print-nysida">
            <div className="sektion-huvud">
              <span className="sektion-ikon" aria-hidden="true">🔑</span>
              <h2>Begrepp</h2>
            </div>
            <dl className="begreppslista">
              {k.begrepp.map((b) => (
                <div key={b.begrepp}>
                  <dt>{b.begrepp}</dt>
                  <dd>{b.forklaring}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="sektion sektion-instudering print-nysida">
            <div className="sektion-huvud">
              <span className="sektion-ikon" aria-hidden="true">💬</span>
              <h2>Instuderingsfrågor</h2>
            </div>
            <ol className="fragelista">
              {k.instuderingsfragor.map((f) => (
                <li key={f.id}>{f.fraga}</li>
              ))}
            </ol>
          </section>

          <section className="sektion sektion-ovning print-nysida">
            <div className="sektion-huvud">
              <span className="sektion-ikon" aria-hidden="true">✏️</span>
              <h2>Övningsuppgifter</h2>
            </div>
            <div className="uppgiftslista">
              {k.ovningsuppgifter.map((o) => (
                <div key={o.id} className="uppgiftskort">
                  <div className="uppgiftskort-meta">
                    <span className={`niva-badge niva-${o.niva}`} title={`Nivå ${o.niva}`}>
                      {o.niva}
                    </span>
                    {(o.formaga ?? []).map((f) => (
                      <span key={f} className="formaga-chip">
                        {f}
                      </span>
                    ))}
                  </div>
                  <p>{o.uppgift}</p>
                  <details className="facit">
                    <summary>Visa facit</summary>
                    <p>{o.facit}</p>
                  </details>
                </div>
              ))}
            </div>
          </section>

          {k.apl_uppgifter.length > 0 && (
            <section className="sektion sektion-apl print-nysida">
              <div className="sektion-huvud">
                <span className="sektion-ikon" aria-hidden="true">🧰</span>
                <h2>APL-uppgifter</h2>
              </div>
              <div className="apl-lista">
                {k.apl_uppgifter.map((a, i) => (
                  <div key={a.id} className="apl-kort">
                    <div className="apl-kort-huvud">
                      <span className="apl-nr" aria-hidden="true">{i + 1}</span>
                      <h3>Uppgift {i + 1} av {k.apl_uppgifter.length}</h3>
                    </div>
                    <p>{a.uppdrag}</p>
                    <p>
                      <span className="apl-etikett">Har du ingen APL-plats just nu?</span>{' '}
                      {a.alternativ_utan_apl}
                    </p>
                    <p>
                      <span className="apl-etikett">Redovisning:</span> {a.redovisning}
                    </p>
                    {a.elevmaterial && (
                      <div className="apl-material apl-material-inline">
                        <h4>{a.elevmaterial.rubrik}</h4>
                        <AplMaterialInnehall material={a.elevmaterial} />
                      </div>
                    )}
                    {a.handledarinfo && (
                      <p className="apl-handledarinfo">
                        <span className="apl-etikett">Till din handledare:</span> {a.handledarinfo}
                      </p>
                    )}
                    {a.bedomningsstod && (
                      <details className="facit">
                        <summary>Visa bedömningsstöd</summary>
                        <p>{a.bedomningsstod}</p>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </article>
      ))}
    </div>
  )
}
